import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { ArrowLeft, Clock, CheckCircle, XCircle, Trophy, AlertCircle, GripVertical, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';

// ===== Sub-components for each activity type =====

const SeleccionMultipleResolver = ({ preguntas, respuestas, onChange, disabled }) => (
  <div className="space-y-6">
    {preguntas.map((pregunta, index) => (
      <motion.div
        key={pregunta.id}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: index * 0.08 }}
      >
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">{index + 1}. {pregunta.texto}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {pregunta.opciones.map((opcion, opcionIndex) => (
              <label
                key={opcionIndex}
                className={`flex items-center gap-3 p-4 border-2 rounded-lg cursor-pointer transition-all hover:bg-muted/50 ${
                  respuestas[pregunta.id] === opcionIndex
                    ? 'border-primary bg-primary/5'
                    : 'border-border'
                }`}
                data-testid={`opcion-${index}-${opcionIndex}`}
              >
                <input
                  type="radio"
                  name={`pregunta-${pregunta.id}`}
                  checked={respuestas[pregunta.id] === opcionIndex}
                  onChange={() => onChange(pregunta.id, opcionIndex)}
                  className="h-4 w-4"
                  disabled={disabled}
                />
                <span className="flex-1">{opcion}</span>
              </label>
            ))}
          </CardContent>
        </Card>
      </motion.div>
    ))}
  </div>
);

const CompletarEspaciosResolver = ({ espacios, respuestas, onChange, disabled }) => (
  <div className="space-y-6">
    {espacios.map((espacio, index) => {
      const partes = espacio.texto.split('[___]');
      const numBlanks = partes.length - 1;
      const currentAnswers = respuestas[espacio.id] || Array(numBlanks).fill('');

      return (
        <motion.div
          key={espacio.id}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: index * 0.08 }}
        >
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Texto {index + 1}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-base leading-relaxed flex flex-wrap items-center gap-1">
                {partes.map((parte, i) => (
                  <React.Fragment key={i}>
                    <span>{parte}</span>
                    {i < numBlanks && (
                      <Input
                        className="inline-flex w-40 mx-1"
                        value={currentAnswers[i] || ''}
                        onChange={(e) => {
                          const newAnswers = [...currentAnswers];
                          newAnswers[i] = e.target.value;
                          onChange(espacio.id, newAnswers);
                        }}
                        placeholder={`Espacio ${i + 1}`}
                        disabled={disabled}
                        data-testid={`blank-${index}-${i}`}
                      />
                    )}
                  </React.Fragment>
                ))}
              </div>
            </CardContent>
          </Card>
        </motion.div>
      );
    })}
  </div>
);

const RelacionarColumnasResolver = ({ columnas, respuestas, onChange, disabled }) => {
  const izquierda = columnas.izquierda || [];
  const derecha = columnas.derecha || [];

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Relaciona cada elemento de la izquierda con su pareja de la derecha</CardTitle>
          <CardDescription>Selecciona la opción correcta para cada elemento</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {izquierda.map((item, i) => (
            <motion.div
              key={item.id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: i * 0.1 }}
              className="flex items-center gap-4 p-3 border-2 rounded-lg border-border"
            >
              <div className="flex-1 font-medium">{item.texto}</div>
              <ArrowRight className="h-5 w-5 text-muted-foreground shrink-0" />
              <select
                className="flex h-10 w-56 rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={respuestas[String(i)] ?? ''}
                onChange={(e) => onChange(String(i), parseInt(e.target.value))}
                disabled={disabled}
                data-testid={`match-select-${i}`}
              >
                <option value="">-- Seleccionar --</option>
                {derecha.map((d, j) => (
                  <option key={d.id} value={j}>{d.texto}</option>
                ))}
              </select>
            </motion.div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
};

const OrdenarSecuenciaResolver = ({ secuenciaOriginal, respuestas, onChange, disabled }) => {
  const shuffled = useMemo(() => {
    if (respuestas.orden && respuestas.orden.length > 0) return null;
    const items = secuenciaOriginal.map(p => ({ ...p }));
    for (let i = items.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [items[i], items[j]] = [items[j], items[i]];
    }
    return items;
  }, [secuenciaOriginal, respuestas.orden]);

  const [items, setItems] = useState(() => {
    if (respuestas.orden && respuestas.orden.length > 0) {
      return respuestas.orden.map(id => secuenciaOriginal.find(p => p.id === id)).filter(Boolean);
    }
    return shuffled || secuenciaOriginal;
  });

  const handleDragEnd = (result) => {
    if (!result.destination || disabled) return;
    const newItems = Array.from(items);
    const [reordered] = newItems.splice(result.source.index, 1);
    newItems.splice(result.destination.index, 0, reordered);
    setItems(newItems);
    onChange('orden', newItems.map(i => i.id));
  };

  useEffect(() => {
    if (!respuestas.orden || respuestas.orden.length === 0) {
      onChange('orden', items.map(i => i.id));
    }
  }, [items, onChange, respuestas.orden]);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Ordena los pasos en la secuencia correcta</CardTitle>
        <CardDescription>Arrastra y suelta para reordenar</CardDescription>
      </CardHeader>
      <CardContent>
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="secuencia">
            {(provided) => (
              <div {...provided.droppableProps} ref={provided.innerRef} className="space-y-2">
                {items.map((paso, index) => (
                  <Draggable key={paso.id} draggableId={paso.id} index={index} isDragDisabled={disabled}>
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        className={`flex items-center gap-3 p-4 border-2 rounded-lg transition-all ${
                          snapshot.isDragging
                            ? 'border-primary bg-primary/10 shadow-lg'
                            : 'border-border bg-card'
                        }`}
                        data-testid={`step-${index}`}
                      >
                        <GripVertical className="h-5 w-5 text-muted-foreground shrink-0" />
                        <span className="font-bold text-primary w-8">{index + 1}</span>
                        <span className="flex-1">{paso.texto}</span>
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </CardContent>
    </Card>
  );
};

const LaboratorioGuiadoResolver = ({ laboratorio, respuestas, onChange, disabled }) => {
  const pasos = laboratorio.pasos || [];
  const [pasoActual, setPasoActual] = useState(0);

  return (
    <div className="space-y-4">
      {laboratorio.recursos && (
        <Card className="border-2 border-blue-500/30 bg-blue-500/5">
          <CardHeader>
            <CardTitle className="text-base">Recursos necesarios</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm whitespace-pre-wrap">{laboratorio.recursos}</p>
          </CardContent>
        </Card>
      )}

      {/* Progress bar */}
      <div className="flex items-center gap-2">
        {pasos.map((_, i) => (
          <div
            key={i}
            className={`h-2 flex-1 rounded-full transition-colors ${
              respuestas[pasos[i]?.id] ? 'bg-green-500' : i === pasoActual ? 'bg-primary' : 'bg-muted'
            }`}
          />
        ))}
      </div>

      {pasos.map((paso, index) => (
        <motion.div
          key={paso.id}
          initial={false}
          animate={{ height: index === pasoActual ? 'auto' : 0, opacity: index === pasoActual ? 1 : 0 }}
          className={index === pasoActual ? '' : 'overflow-hidden h-0'}
        >
          {index === pasoActual && (
            <Card className="border-2 border-primary/30">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">Paso {index + 1}: {paso.titulo}</CardTitle>
                  <Badge variant="outline">{index + 1}/{pasos.length}</Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm whitespace-pre-wrap">{paso.descripcion}</p>
                {paso.verificacion && (
                  <div className="p-3 rounded-lg bg-muted/50 border border-border">
                    <p className="text-xs font-medium text-muted-foreground mb-1">Criterio de verificacion:</p>
                    <p className="text-sm">{paso.verificacion}</p>
                  </div>
                )}
                <div className="flex items-center gap-4 pt-2">
                  <label className="flex items-center gap-2 cursor-pointer" data-testid={`lab-check-${index}`}>
                    <input
                      type="checkbox"
                      checked={!!respuestas[paso.id]}
                      onChange={(e) => onChange(paso.id, e.target.checked)}
                      className="h-5 w-5"
                      disabled={disabled}
                    />
                    <span className="font-medium">He completado este paso</span>
                  </label>
                </div>
                <div className="flex gap-2 justify-between pt-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPasoActual(Math.max(0, index - 1))}
                    disabled={index === 0}
                  >
                    Anterior
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => setPasoActual(Math.min(pasos.length - 1, index + 1))}
                    disabled={index === pasos.length - 1}
                  >
                    Siguiente
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </motion.div>
      ))}

      {/* Summary of steps */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Resumen de Pasos</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {pasos.map((paso, i) => (
              <div
                key={paso.id}
                className={`flex items-center gap-3 p-2 rounded cursor-pointer transition-colors hover:bg-muted/50 ${i === pasoActual ? 'bg-primary/10' : ''}`}
                onClick={() => setPasoActual(i)}
              >
                {respuestas[paso.id] ? (
                  <CheckCircle className="h-5 w-5 text-green-500 shrink-0" />
                ) : (
                  <div className="h-5 w-5 rounded-full border-2 border-muted-foreground shrink-0" />
                )}
                <span className={`text-sm ${respuestas[paso.id] ? 'line-through text-muted-foreground' : ''}`}>
                  {paso.titulo}
                </span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const HtmlExternoResolver = ({ contenidoHtml, respuestas, onChange, disabled }) => (
  <div className="space-y-4">
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Actividad Interactiva</CardTitle>
        <CardDescription>Completa la actividad en el panel de abajo</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="border-2 border-border rounded-lg overflow-hidden">
          <iframe
            srcDoc={contenidoHtml}
            className="w-full bg-white"
            style={{ height: '500px' }}
            sandbox="allow-scripts"
            title="Actividad HTML"
            data-testid="html-iframe"
          />
        </div>
        <div className="mt-4">
          <label className="flex items-center gap-2 cursor-pointer" data-testid="html-completed-check">
            <input
              type="checkbox"
              checked={!!respuestas.completed}
              onChange={(e) => onChange('completed', e.target.checked)}
              className="h-5 w-5"
              disabled={disabled}
            />
            <span className="font-medium">He completado esta actividad</span>
          </label>
        </div>
      </CardContent>
    </Card>
  </div>
);

// ===== Feedback components =====

const FeedbackSeleccionMultiple = ({ preguntas, respuestas }) => (
  <div className="space-y-4">
    <h3 className="font-semibold text-lg">Resultados por Pregunta</h3>
    {preguntas.map((pregunta, index) => {
      const respuestaUsuario = respuestas[pregunta.id];
      const esCorrecta = respuestaUsuario === pregunta.respuesta_correcta;
      return (
        <Card key={pregunta.id} className={`border-2 ${esCorrecta ? 'border-green-500 bg-green-50 dark:bg-green-950' : 'border-red-500 bg-red-50 dark:bg-red-950'}`}>
          <CardHeader>
            <div className="flex items-start justify-between">
              <CardTitle className="text-lg">Pregunta {index + 1}: {pregunta.texto}</CardTitle>
              {esCorrecta ? <CheckCircle className="h-6 w-6 text-green-600 shrink-0" /> : <XCircle className="h-6 w-6 text-red-600 shrink-0" />}
            </div>
          </CardHeader>
          <CardContent className="space-y-2">
            <p className="text-sm">
              <span className="font-medium">Tu respuesta:</span> {pregunta.opciones[respuestaUsuario]}
            </p>
            {!esCorrecta && (
              <p className="text-sm">
                <span className="font-medium">Respuesta correcta:</span> {pregunta.opciones[pregunta.respuesta_correcta]}
              </p>
            )}
            {pregunta.retroalimentacion && (
              <p className="text-sm text-muted-foreground italic mt-2">{pregunta.retroalimentacion}</p>
            )}
          </CardContent>
        </Card>
      );
    })}
  </div>
);

// ===== Main Component =====

const ResolverActividad = () => {
  const navigate = useNavigate();
  const { actividadId, asignacionId } = useParams();
  const { user } = useAuth();

  const [actividad, setActividad] = useState(null);
  const [asignacion, setAsignacion] = useState(null);
  const [intentos, setIntentos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [enviando, setEnviando] = useState(false);
  const [mostrarResultado, setMostrarResultado] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [respuestas, setRespuestas] = useState({});

  const fetchData = useCallback(async () => {
    try {
      const [asignacionesRes, intentosRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/asignaciones/estudiante`, { withCredentials: true }),
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/intentos/actividad/${actividadId}`, { withCredentials: true })
      ]);

      const asignacionEncontrada = asignacionesRes.data.find(a => a.asignacion_id === asignacionId);
      setAsignacion(asignacionEncontrada);
      setActividad(asignacionEncontrada?.actividad);
      setIntentos(intentosRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Error al cargar actividad');
    } finally {
      setLoading(false);
    }
  }, [actividadId, asignacionId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleRespuestaChange = useCallback((key, value) => {
    setRespuestas(prev => ({ ...prev, [key]: value }));
  }, []);

  const isComplete = () => {
    if (!actividad) return false;
    const tipo = actividad.tipo_actividad;
    const contenido = actividad.contenido_json || {};

    if (tipo === 'seleccion_multiple') {
      const preguntas = contenido.preguntas || [];
      return preguntas.every(p => respuestas[p.id] !== undefined);
    }
    if (tipo === 'completar_espacios') {
      const espacios = contenido.espacios || [];
      return espacios.every(e => {
        const numBlanks = (e.texto.match(/\[___\]/g) || []).length;
        const answers = respuestas[e.id] || [];
        return answers.length === numBlanks && answers.every(a => a && a.trim() !== '');
      });
    }
    if (tipo === 'relacionar_columnas') {
      const total = (contenido.columnas?.izquierda || []).length;
      return Array.from({ length: total }, (_, i) => String(i)).every(k => respuestas[k] !== undefined && respuestas[k] !== '');
    }
    if (tipo === 'ordenar_secuencia') {
      return respuestas.orden && respuestas.orden.length > 0;
    }
    if (tipo === 'laboratorio_guiado') {
      const pasos = contenido.laboratorio?.pasos || [];
      return pasos.every(p => respuestas[p.id] === true);
    }
    if (tipo === 'html_externo') {
      return !!respuestas.completed;
    }
    return false;
  };

  const handleEnviar = async () => {
    if (!actividad) return;

    if (!isComplete()) {
      toast.error('Por favor completa toda la actividad antes de enviar');
      return;
    }

    if (actividad.permite_intentos && intentos.length >= actividad.max_intentos) {
      toast.error('Has alcanzado el numero maximo de intentos');
      return;
    }

    setEnviando(true);
    try {
      const { data } = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/intentos`,
        {
          actividad_id: actividadId,
          asignacion_id: asignacionId,
          respuestas
        },
        { withCredentials: true }
      );

      setResultado(data);
      setMostrarResultado(true);

      if (actividad.mostrar_retroalimentacion_inmediata) {
        toast.success('Actividad completada!');
      } else {
        toast.success('Respuestas enviadas. Espera la retroalimentacion del docente.');
        setTimeout(() => navigate('/estudiante'), 2000);
      }
    } catch (error) {
      console.error('Error submitting intento:', error);
      toast.error(error.response?.data?.detail || 'Error al enviar respuestas');
    } finally {
      setEnviando(false);
    }
  };

  const getNivelLogroColor = (nivel) => {
    const colors = { 'Inicial': 'bg-red-500', 'Intermedio': 'bg-yellow-500', 'Avanzado': 'bg-green-500' };
    return colors[nivel] || 'bg-gray-500';
  };

  const getTipoLabel = (tipo) => {
    const labels = {
      'seleccion_multiple': 'Seleccion Multiple',
      'completar_espacios': 'Completar Espacios',
      'relacionar_columnas': 'Relacionar Columnas',
      'ordenar_secuencia': 'Ordenar Secuencia',
      'laboratorio_guiado': 'Laboratorio Guiado',
      'html_externo': 'HTML/JavaScript'
    };
    return labels[tipo] || tipo;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Cargando actividad...</p>
        </div>
      </div>
    );
  }

  if (!actividad || !asignacion) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Card>
          <CardContent className="pt-6">
            <p className="text-muted-foreground">Actividad no encontrada</p>
            <Button onClick={() => navigate('/estudiante')} className="mt-4" data-testid="btn-volver-not-found">Volver</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const tipo = actividad.tipo_actividad;
  const contenido = actividad.contenido_json || {};
  const yaCompleto = intentos.length > 0;

  const renderActividad = () => {
    switch (tipo) {
      case 'seleccion_multiple':
        return (
          <SeleccionMultipleResolver
            preguntas={contenido.preguntas || []}
            respuestas={respuestas}
            onChange={handleRespuestaChange}
            disabled={yaCompleto && !actividad.mostrar_retroalimentacion_inmediata}
          />
        );
      case 'completar_espacios':
        return (
          <CompletarEspaciosResolver
            espacios={contenido.espacios || []}
            respuestas={respuestas}
            onChange={handleRespuestaChange}
            disabled={yaCompleto && !actividad.mostrar_retroalimentacion_inmediata}
          />
        );
      case 'relacionar_columnas':
        return (
          <RelacionarColumnasResolver
            columnas={contenido.columnas || { izquierda: [], derecha: [] }}
            respuestas={respuestas}
            onChange={handleRespuestaChange}
            disabled={yaCompleto && !actividad.mostrar_retroalimentacion_inmediata}
          />
        );
      case 'ordenar_secuencia':
        return (
          <OrdenarSecuenciaResolver
            secuenciaOriginal={contenido.secuencia || []}
            respuestas={respuestas}
            onChange={handleRespuestaChange}
            disabled={yaCompleto && !actividad.mostrar_retroalimentacion_inmediata}
          />
        );
      case 'laboratorio_guiado':
        return (
          <LaboratorioGuiadoResolver
            laboratorio={contenido.laboratorio || { pasos: [], recursos: '' }}
            respuestas={respuestas}
            onChange={handleRespuestaChange}
            disabled={yaCompleto && !actividad.mostrar_retroalimentacion_inmediata}
          />
        );
      case 'html_externo':
        return (
          <HtmlExternoResolver
            contenidoHtml={actividad.contenido_html || ''}
            respuestas={respuestas}
            onChange={handleRespuestaChange}
            disabled={yaCompleto && !actividad.mostrar_retroalimentacion_inmediata}
          />
        );
      default:
        return <p className="text-muted-foreground">Tipo de actividad no soportado: {tipo}</p>;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-background/80 border-b border-border">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/estudiante')}
                data-testid="back-button"
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">{actividad.titulo}</h1>
                <p className="text-sm text-muted-foreground">{getTipoLabel(tipo)}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {actividad.permite_intentos && (
                <Badge variant="outline">
                  Intentos: {intentos.length}/{actividad.max_intentos}
                </Badge>
              )}
              <Badge>
                <Clock className="h-3 w-3 mr-1" />
                Cierra: {new Date(asignacion.fecha_cierre).toLocaleDateString()}
              </Badge>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 max-w-4xl">
        {mostrarResultado && resultado ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="space-y-6"
          >
            <Card className="border-2 border-primary">
              <CardHeader className="text-center">
                <div className="mx-auto mb-4">
                  <Trophy className="h-20 w-20 text-primary mx-auto" />
                </div>
                <CardTitle className="text-3xl">Actividad Completada!</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  {resultado.puntaje_total !== null && (
                    <Card>
                      <CardHeader className="pb-3">
                        <CardDescription className="text-xs uppercase">Puntaje</CardDescription>
                        <CardTitle className="text-4xl" data-testid="puntaje-result">
                          {Math.round(resultado.puntaje_total)}%
                        </CardTitle>
                      </CardHeader>
                    </Card>
                  )}

                  {resultado.nivel_logro && (
                    <Card>
                      <CardHeader className="pb-3">
                        <CardDescription className="text-xs uppercase">Nivel de Logro</CardDescription>
                        <Badge className={`${getNivelLogroColor(resultado.nivel_logro)} text-white text-lg px-4 py-2`} data-testid="nivel-logro-result">
                          {resultado.nivel_logro}
                        </Badge>
                      </CardHeader>
                    </Card>
                  )}
                </div>

                {resultado.puntaje_total === null && tipo === 'html_externo' && (
                  <Card className="border-2 border-yellow-500/30 bg-yellow-500/5">
                    <CardContent className="pt-6">
                      <p className="text-sm text-center">Esta actividad sera revisada por tu docente. Recibiras tu calificacion pronto.</p>
                    </CardContent>
                  </Card>
                )}

                {actividad.mostrar_retroalimentacion_inmediata && tipo === 'seleccion_multiple' && (
                  <FeedbackSeleccionMultiple preguntas={contenido.preguntas || []} respuestas={respuestas} />
                )}

                <div className="flex gap-2 justify-center">
                  {actividad.permite_intentos && intentos.length < actividad.max_intentos && (
                    <Button
                      onClick={() => {
                        setMostrarResultado(false);
                        setRespuestas({});
                        setResultado(null);
                        fetchData();
                      }}
                      data-testid="btn-reintentar"
                    >
                      Intentar de Nuevo
                    </Button>
                  )}
                  <Button variant="outline" onClick={() => navigate('/estudiante')} data-testid="btn-volver-dashboard">
                    Volver al Dashboard
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        ) : (
          <div className="space-y-6">
            {actividad.permite_logro && actividad.indicador_evaluacion && (
              <Card>
                <CardHeader>
                  <CardTitle>Criterio de Evaluación</CardTitle>
                  <CardDescription>Indicador y niveles de logro definidos por el docente</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <h4 className="font-semibold mb-2">Indicador de evaluación</h4>
                    <p className="text-muted-foreground whitespace-pre-wrap">{actividad.indicador_evaluacion}</p>
                  </div>
                  {actividad.rubrica_logro && (
                    <div className="grid gap-3">
                      {['Inicial', 'Intermedio', 'Avanzado'].map((nivel) => (
                        <div key={nivel} className="rounded-lg border border-border p-3">
                          <h5 className="font-semibold mb-1">{nivel}</h5>
                          <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                            {actividad.rubrica_logro[nivel] || 'Sin descriptor definido'}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {actividad.instrucciones && (
              <Card>
                <CardHeader>
                  <CardTitle>Instrucciones</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground whitespace-pre-wrap">{actividad.instrucciones}</p>
                </CardContent>
              </Card>
            )}

            {yaCompleto && !actividad.mostrar_retroalimentacion_inmediata && (
              <Card className="border-2 border-yellow-500 bg-yellow-50 dark:bg-yellow-950">
                <CardHeader>
                  <div className="flex items-center gap-2">
                    <AlertCircle className="h-5 w-5 text-yellow-600" />
                    <CardTitle>Actividad ya completada</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm">Has enviado tus respuestas. Espera la retroalimentacion de tu docente.</p>
                </CardContent>
              </Card>
            )}

            {renderActividad()}

            {(!yaCompleto || (actividad.permite_intentos && intentos.length < actividad.max_intentos)) && (
              <div className="sticky bottom-4 bg-background/80 backdrop-blur-xl p-4 rounded-lg border border-border">
                <Button
                  onClick={handleEnviar}
                  disabled={enviando || !isComplete()}
                  className="w-full"
                  size="lg"
                  data-testid="btn-enviar-respuestas"
                >
                  {enviando ? 'Enviando...' : 'Enviar Respuestas'}
                </Button>
                {!isComplete() && (
                  <p className="text-sm text-center text-muted-foreground mt-2">
                    Completa toda la actividad para continuar
                  </p>
                )}
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default ResolverActividad;
